import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { EmailStatusPanel } from './EmailStatusPanel';

const API = process.env.REACT_APP_BACKEND_URL;
const ACTIONS = [
  { s: 'applied', label: 'Applied', cls: 'border-emerald-500/40 text-emerald-300' },
  { s: 'open', label: 'Open', cls: 'border-amber-500/40 text-amber-300' },
  { s: 'declined', label: 'Not adopted', cls: 'border-white/20 text-gray-300' },
];
const FILTERS = ['all', 'new', 'applied', 'open', 'declined'];
const usDate = (iso) => { const d = new Date(iso); return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`; };
const field = (msg, key) => (msg.match(new RegExp(`^${key}: (.*)$`, 'm')) || [])[1];
const body = (msg) => msg.split('\n\nSection:')[0];

function CommentCard({ c, onSet, busy }) {
  return (
    <li className="rounded-2xl border border-white/10 bg-white/[0.02] p-5" data-testid={`inbox-comment-${c.id}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400 mb-2">
        <span className="font-semibold text-white text-sm">{c.name}</span>
        <span>{c.email}</span>
        <span>{usDate(c.created_at)}</span>
        <span className="rounded-full border border-white/15 px-2 py-0.5">{c.subject.replace('Claim Records comment: ', '')}</span>
        <span className="rounded-full border border-white/15 px-2 py-0.5" data-testid={`inbox-credit-${c.id}`}>{field(c.message, 'Credit') || 'Credit not stated'}</span>
      </div>
      <p className="text-sm text-gray-200 whitespace-pre-wrap leading-relaxed mb-4">{body(c.message)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 mr-1" data-testid={`inbox-status-${c.id}`}>Status: {c.triage_status}</span>
        {ACTIONS.map((a) => (
          <button key={a.s} type="button" disabled={busy || c.triage_status === a.s} onClick={() => onSet(c.id, a.s)} className={`rounded-full border px-3 py-1 text-xs transition-colors disabled:opacity-40 hover:bg-white/5 ${a.cls}`} data-testid={`inbox-mark-${a.s}-${c.id}`}>Mark {a.label}</button>
        ))}
      </div>
    </li>
  );
}

export function ClaimCommentsInbox() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/claim-comments`, { headers: { Authorization: `Bearer ${token}` } });
      const b = await res.json();
      if (!res.ok) throw new Error(b?.detail || 'Failed to load comments');
      setData(b);
    } catch (e) { setError(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (id, status) => {
    setBusy(id);
    const res = await fetch(`${API}/api/admin/claim-comments/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ status }) });
    if (res.ok) await load(); else setError('Could not update the comment.');
    setBusy(null);
  };
  if (error) return <p className="text-sm text-red-300" data-testid="inbox-error">{error}</p>;
  if (!data) return <p className="text-sm text-gray-400" data-testid="inbox-loading">Loading comments...</p>;
  const list = data.comments.filter((c) => filter === 'all' || c.triage_status === filter);
  return (
    <div className="space-y-5" data-testid="claim-comments-inbox">
      <div>
        <h2 className="text-lg font-semibold">Claim Records comments</h2>
        <p className="text-sm text-gray-400">Sent through the form on /specs/claim-records/comments. Marking a comment here is private. To show it publicly, add a summary to the public comment tracker.</p>
      </div>
      <EmailStatusPanel />
      <div className="flex flex-wrap gap-2" data-testid="inbox-filters">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} className={`rounded-full border px-3 py-1 text-xs ${filter === f ? 'border-amber-400/60 text-white' : 'border-white/15 text-gray-400 hover:text-white'}`} data-testid={`inbox-filter-${f}`}>
            {f === 'all' ? `All (${data.total})` : `${f === 'declined' ? 'Not adopted' : f.charAt(0).toUpperCase() + f.slice(1)} (${data.counts[f]})`}
          </button>
        ))}
      </div>
      <ul className="space-y-3">
        {list.map((c) => <CommentCard key={c.id} c={c} onSet={setStatus} busy={busy === c.id} />)}
        {!list.length && <li className="text-sm text-gray-500" data-testid="inbox-empty">No comments here.</li>}
      </ul>
    </div>
  );
}
